import { createSlice } from "@reduxjs/toolkit";
import { getBillingPlanAction } from "../../actions";
import {
  billingupgradePreviewAction,
  getCheckinBusinessBillingSummaryAction,
  getCheckinBusinessBillingTableAction,
  getSubscriptionDetailsAction,
  getBillingInvoicesAction,
} from "../../actions/UserActions/BillingActions";

export interface IFeature {
    title: string;
    subFeatures: string[];
    _id: string;
}

export interface IGetBillingPlanData {
    _id: string;
    planId: string;
    name: string;
    yearly: boolean;
    disabled: boolean;
    monthly_price: number;
    priceId_monthly: string;
    priceId_yearly: string;
    yearly_price: number;
    currency: string;
    category: string;
    credits: number;
    gbpConnections: number;
    citationAllowed: boolean;
    features: IFeature[];
    createdAt: string;
    updatedAt: string;
    __v: number;
}

export interface ICheckinBillingSummary {
    total: number;
    active: number;
    trialing: number;
    failed: number;
    managed: number;
    monthly_total: number;
    price_range: { min?: number; max?: number } | string | null;
}

export interface ICheckinBillingTableItem {
    _id: string;
    business_name: string;
    address: string;
    status: string;
    price: number;
    next_charge: string | null;
    trial_days_remaining: number | null;
}

export interface ICheckinBillingTablePagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

export interface ISubscriptionDetails {
    plan_name?: string;
    plan_id?: string;
    plan_price?: number;
    status?: string;
    subscription_status?: string;
    cancel_at_period_end?: boolean;
    cancel_at?: string | null;
    next_billing_date?: string | null;
    credits?: number;
    purchased_credits?: number;
    monthly_credits?: number;
    has_payment_method?: boolean;
    card_brand?: string | null;
    card_last4?: string | null;
    amount?: number;
    currency?: string;
    interval?: string;
    current_period_start?: string | null;
    current_period_end?: string | null;
    portal_url?: string | null;
}

export interface IBillingInvoice {
    date: string;
    name: string;
    object: string;
    amount: number;
    status: string;
    url: string;
    category: string;
}

export interface IBillingInvoicesPagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface IGetBillingPlanState {
    getBillingPlanData: IGetBillingPlanData[]
    getBillingPlanDataLoading: boolean
    billingUpgradePreviewData: any
    billingUpgradePreviewLoading: boolean
    checkinBillingSummary: ICheckinBillingSummary | null
    checkinBillingSummaryLoading: boolean
    checkinBillingTable: ICheckinBillingTableItem[]
    checkinBillingTablePagination: ICheckinBillingTablePagination | null
    checkinBillingTableLoading: boolean
    subscriptionDetails: ISubscriptionDetails | null
    subscriptionDetailsLoading: boolean
    billingInvoices: IBillingInvoice[]
    billingInvoicesPagination: IBillingInvoicesPagination | null
    billingInvoicesLoading: boolean
}

const initialState: IGetBillingPlanState = {
    getBillingPlanData: [],
    getBillingPlanDataLoading: false,
    billingUpgradePreviewData: [],
    billingUpgradePreviewLoading: false,
    checkinBillingSummary: null,
    checkinBillingSummaryLoading: false,
    checkinBillingTable: [],
    checkinBillingTablePagination: null,
    checkinBillingTableLoading: false,
    subscriptionDetails: null,
    subscriptionDetailsLoading: false,
    billingInvoices: [],
    billingInvoicesPagination: null,
    billingInvoicesLoading: false,
}

const billingSlice = createSlice({
    name: "billing",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder

            //get billing plan
            .addCase(getBillingPlanAction.pending, (state) => {
                state.getBillingPlanDataLoading = true
            })
            .addCase(getBillingPlanAction.fulfilled, (state, action) => {
                state.getBillingPlanDataLoading = false
                state.getBillingPlanData = action.payload.data
            })
            .addCase(getBillingPlanAction.rejected, (state, action) => {
                state.getBillingPlanDataLoading = false
            })

            .addCase(billingupgradePreviewAction.pending, (state) => {
                state.billingUpgradePreviewLoading = true
            })
            .addCase(billingupgradePreviewAction.fulfilled, (state, action) => {
                state.billingUpgradePreviewLoading = false
                state.billingUpgradePreviewData = action.payload
            })
            .addCase(billingupgradePreviewAction.rejected, (state, action) => {
                state.billingUpgradePreviewLoading = false
            })

            .addCase(getCheckinBusinessBillingSummaryAction.pending, (state) => {
                state.checkinBillingSummaryLoading = true
            })
            .addCase(getCheckinBusinessBillingSummaryAction.fulfilled, (state, action) => {
                state.checkinBillingSummaryLoading = false
                state.checkinBillingSummary = action.payload.data
            })
            .addCase(getCheckinBusinessBillingSummaryAction.rejected, (state) => {
                state.checkinBillingSummaryLoading = false
            })

            .addCase(getCheckinBusinessBillingTableAction.pending, (state) => {
                state.checkinBillingTableLoading = true
            })
            .addCase(getCheckinBusinessBillingTableAction.fulfilled, (state, action) => {
                state.checkinBillingTableLoading = false
                state.checkinBillingTable = action.payload.data.businesses
                state.checkinBillingTablePagination = action.payload.data.pagination
            })
            .addCase(getCheckinBusinessBillingTableAction.rejected, (state) => {
                state.checkinBillingTableLoading = false
            })

            .addCase(getSubscriptionDetailsAction.pending, (state) => {
                state.subscriptionDetailsLoading = true
            })
            .addCase(getSubscriptionDetailsAction.fulfilled, (state, action) => {
                state.subscriptionDetailsLoading = false
                state.subscriptionDetails = action.payload.data
            })
            .addCase(getSubscriptionDetailsAction.rejected, (state) => {
                state.subscriptionDetailsLoading = false
            })

            .addCase(getBillingInvoicesAction.pending, (state) => {
                state.billingInvoicesLoading = true
            })
            .addCase(getBillingInvoicesAction.fulfilled, (state, action) => {
                state.billingInvoicesLoading = false
                state.billingInvoices = action.payload?.data?.invoices ?? []
                state.billingInvoicesPagination = action.payload?.data?.pagination ?? null
            })
            .addCase(getBillingInvoicesAction.rejected, (state) => {
                state.billingInvoicesLoading = false
            })
    }
})

export default billingSlice.reducer